import { useMemo } from 'react';
import { useAuth0 as useSdkAuth0 } from '@auth0/auth0-react';
import { clearImpersonation, getImpersonation, isImpersonationExpired } from './impersonation';

type SdkAuth0 = ReturnType<typeof useSdkAuth0>;

/**
 * Drop-in replacement for the SDK hook. Outside impersonation it IS the SDK hook.
 * In an impersonation tab it reports the impersonated user as authenticated and
 * hands every caller the impersonation token instead of an Auth0 one, so the 50+
 * call sites that do `getAccessTokenSilently()` need no change. Logging out ends
 * the impersonation (the tab is the session).
 *
 * The overrides are memoized per token: many screens list `getAccessTokenSilently`
 * in useEffect dependencies, so a fresh function on every render would re-run
 * those effects forever (that was an 86k-request loop in dev).
 */
export function useAuth0(): SdkAuth0 {
    const sdk = useSdkAuth0();
    const imp = getImpersonation();
    const active = !!imp && !isImpersonationExpired(imp);
    const token = active ? imp!.token : null;
    const sub = active ? imp!.sub : null;
    const email = active ? imp!.targetEmail : null;

    const overrides = useMemo<Partial<SdkAuth0> | null>(() => {
        if (!token || !sub) return null;
        return {
            isAuthenticated: true,
            isLoading: false,
            error: undefined,
            user: { sub, email: email || undefined, name: email || sub, email_verified: true },
            getAccessTokenSilently: (async () => token) as SdkAuth0['getAccessTokenSilently'],
            loginWithRedirect: async () => undefined,
            logout: async () => {
                clearImpersonation();
                window.location.replace('/impersonate#ended');
            },
        };
    }, [token, sub, email]);

    return useMemo(() => (overrides ? { ...sdk, ...overrides } : sdk), [sdk, overrides]);
}
